import { Track, TrackDef } from '../track.js';
import { generateTrack } from '../procgen.js';
import { ionport } from './ionport.js';
import { sunspire } from './sunspire.js';
import { helion } from './helion.js';
import { sandsong } from './sandsong.js';
import { tempest } from './tempest.js';
import { anchorage } from './anchorage.js';
import { boreal } from './boreal.js';
import { skyfall } from './skyfall.js';
import { nullspire } from './nullspire.js';

/**
 * Track registry. Authored tracks register here; procedural tracks build from seeds.
 * Track construction (spline sampling, racing line) is cached — it is deterministic
 * and moderately expensive, and server + client both hit this path.
 */

// roster order = track-select order: beginner → expert
const AUTHORED: TrackDef[] = [
  ionport, // Neon Haven, D1 circuit
  helion, // Helion Core, D2 circuit
  anchorage, // Anchorage-9, D2 wide multiplayer circuit
  sandsong, // Duneworks, D2 sprint
  sunspire, // Neon Haven, D3 technical circuit
  boreal, // Aurora Reach, D3 drift circuit
  tempest, // Stormline, D3 high-speed circuit
  skyfall, // The Ascent, D4 sprint
  nullspire, // Voidgate, D4 expert circuit
];

export function registerTracks(defs: TrackDef[]): void {
  for (const def of defs) {
    if (!AUTHORED.some((d) => d.id === def.id)) AUTHORED.push(def);
  }
}

export function allTrackDefs(): TrackDef[] {
  return [...AUTHORED];
}

export function getTrackDef(id: string): TrackDef | null {
  if (id.startsWith('seed:')) return generateTrack(id.slice(5));
  return AUTHORED.find((d) => d.id === id) ?? null;
}

const trackCache = new Map<string, Track>();

/**
 * Build (or fetch cached) runtime Track. `seed` overrides `trackId` when present.
 * Returns the ionport fallback for unknown ids so a stale save can never crash a session.
 */
export function buildTrack(trackId: string, seed: string | null = null): Track {
  const key = seed ? `seed:${seed}` : trackId;
  const hit = trackCache.get(key);
  if (hit) return hit;
  let def: TrackDef | null;
  if (seed) def = generateTrack(seed);
  else def = getTrackDef(trackId);
  if (!def) def = ionport;
  const track = new Track(def);
  // keep the cache bounded: procedural seeds could otherwise grow forever server-side
  if (trackCache.size > 40) {
    const first = trackCache.keys().next().value;
    if (first !== undefined) trackCache.delete(first);
  }
  trackCache.set(key, track);
  return track;
}
